import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  LinearProgress,
  Tooltip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  OndemandVideo as OndemandVideoIcon,
  Timer as TimerIcon,
  CheckCircle as CheckCircleIcon,
  Block as BlockIcon,
  NotificationsActive as NotificationsActiveIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import useApp from "../../store/contexts/AppContext";

// Styled components with improved consistency
const AdCard = styled(Card)(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === "dark" ? "#1a1a1a" : theme.palette.background.paper,
  borderRadius: theme.spacing(1.5),
  marginBottom: theme.spacing(2),
  position: "relative",
  border: `1px solid ${
    theme.palette.mode === "dark" ? "#333" : theme.palette.divider
  }`,
  overflow: "hidden",
}));

const AdCardHighlight = styled(Box)(({ theme, status }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  height: "6px",
  backgroundColor:
    status === "required"
      ? theme.palette.error.main
      : theme.palette.success.main,
}));

// Enhanced time formatting with hours and minutes
const formatTimeRemaining = (expirySeconds) => {
  if (!expirySeconds || expirySeconds <= 0) return "Expired";

  const hours = Math.floor(expirySeconds / 3600);
  const minutes = Math.floor((expirySeconds % 3600) / 60);

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  } else if (minutes > 0) {
    return `${minutes}m`;
  } else {
    return "Less than a minute";
  }
};

export default function AdStatusIndicator({ onAdRequired, classes }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    appState: { userData },
    checkAdStatusApiCall,
  } = useApp();

  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [adStatus, setAdStatus] = useState({
    requires_ad: false,
    exemption_expires_in: null,
    account_type: "basic",
  });
  const [error, setError] = useState(null);

  // Use refs to track mounted state and intervals
  const isMountedRef = useRef(true);
  const statusIntervalRef = useRef(null);
  const refreshIntervalRef = useRef(null);
  const lastFetchTimeRef = useRef(0);

  // Calculate progress percentage for the timer
  const calculateProgress = () => {
    if (!adStatus.exemption_expires_in) return 0;

    // Assuming ad exemption is typically for 1 hour (3600 seconds)
    const totalExemptionTime = 3600;
    const progress = (adStatus.exemption_expires_in / totalExemptionTime) * 100;

    return Math.min(100, Math.max(0, progress)); // Ensure between 0 and 100
  };

  // Memoize the ad status check function to avoid recreation on each render
  const checkAdStatus = useCallback(async () => {
    // Prevent excessive API calls by enforcing a minimum time between calls
    const now = Date.now();
    if (now - lastFetchTimeRef.current < 5000) {
      // 5 second cooldown
      return;
    }

    // Set the last fetch time
    lastFetchTimeRef.current = now;

    try {
      if (!isMountedRef.current) return;

      setCheckingStatus(true);
      setError(null);

      // Add timeout to prevent infinite loading
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error("Timeout after 5 seconds")), 5000)
      );

      // Race between the API call and timeout
      const response = await Promise.race([
        checkAdStatusApiCall(),
        timeoutPromise,
      ]);

      if (!isMountedRef.current) return;

      if (response && response.success) {
        setAdStatus(response.data);

        // If ad is required, trigger the callback
        if (response.data.requires_ad && onAdRequired) {
          // Use a slight delay to avoid disrupting the loading flow
          setTimeout(() => {
            if (isMountedRef.current) onAdRequired();
          }, 500);
        }
      } else {
        // Fallback if API doesn't return success
        setAdStatus({
          requires_ad: false,
          exemption_expires_in: null,
          account_type: userData?.account_type || "basic",
        });

        if (response && !response.success) {
          setError(response.message || "Failed to check ad status");
        }
      }
    } catch (error) {
      console.error("Error checking ad status:", error);
      if (isMountedRef.current) {
        setError(error.message || "Error checking ad status");
        // Set fallback values on error
        setAdStatus({
          requires_ad: false,
          exemption_expires_in: null,
          account_type: userData?.account_type || "basic",
        });
      }
    } finally {
      if (isMountedRef.current) {
        setCheckingStatus(false);
      }
    }
  }, [checkAdStatusApiCall, onAdRequired, userData?.account_type]);

  // Set up intervals on mount and clean up on unmount
  useEffect(() => {
    // Mark as mounted
    isMountedRef.current = true;

    // Only set up checks for basic users
    if (userData?.id && userData.account_type === "basic") {
      // Initial check
      checkAdStatus();

      // Clear any existing intervals to prevent multiple instances
      if (statusIntervalRef.current) clearInterval(statusIntervalRef.current);
      if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);

      // Set interval for critical time checks (expiring soon)
      statusIntervalRef.current = setInterval(() => {
        if (
          adStatus.exemption_expires_in &&
          adStatus.exemption_expires_in < 300
        ) {
          checkAdStatus();
        }
      }, 30000); // Check every 30 seconds for critical timings

      // Set interval for regular refreshes, but less frequently
      refreshIntervalRef.current = setInterval(checkAdStatus, 60000); // Every minute
    } else {
      setCheckingStatus(false);
    }

    // Cleanup function
    return () => {
      isMountedRef.current = false;
      if (statusIntervalRef.current) clearInterval(statusIntervalRef.current);
      if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);
    };
  }, [userData?.id, userData?.account_type, checkAdStatus]);

  // Only show for basic users
  if (userData?.account_type !== "basic") {
    return null;
  }

  if (checkingStatus && !adStatus.exemption_expires_in) {
    return (
      <AdCard elevation={1}>
        <CardContent
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            py: 3,
          }}
        >
          <CircularProgress size={24} color="secondary" />
          <Typography variant="body2" sx={{ ml: 2 }}>
            Checking ad status...
          </Typography>
        </CardContent>
      </AdCard>
    );
  }

  if (error) {
    return (
      <AdCard elevation={1}>
        <CardContent sx={{ py: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
            <OndemandVideoIcon
              sx={{
                fontSize: "1.5rem",
                mr: 1,
                color: theme.palette.warning.main,
              }}
            />
            <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
              Ad Status
            </Typography>
          </Box>
          <Typography variant="body2" color="error" sx={{ mb: 1 }}>
            {error}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={checkAdStatus}
            sx={{ mt: 1 }}
          >
            Try Again
          </Button>
        </CardContent>
      </AdCard>
    );
  }

  return (
    <AdCard elevation={1}>
      <AdCardHighlight status={adStatus.requires_ad ? "required" : "exempt"} />

      <CardContent sx={{ pt: 3 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mb: 2,
            flexDirection: isMobile ? "column" : "row",
            alignItems: isMobile ? "flex-start" : "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: isMobile ? 1 : 0,
              width: isMobile ? "100%" : "auto",
            }}
          >
            <OndemandVideoIcon
              sx={{
                fontSize: "1.5rem",
                mr: 1,
                color: adStatus.requires_ad
                  ? theme.palette.error.main
                  : theme.palette.success.main,
              }}
            />
            <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
              Ad Status
            </Typography>
          </Box>

          {!isMobile &&
            adStatus.exemption_expires_in &&
            !adStatus.requires_ad && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  ml: "auto",
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(0, 150, 136, 0.12)"
                      : "rgba(0, 150, 136, 0.08)",
                  borderRadius: "16px",
                  padding: "4px 12px",
                }}
              >
                <TimerIcon
                  sx={{
                    fontSize: "0.875rem",
                    mr: 0.5,
                    color: theme.palette.success.main,
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.success.main, fontWeight: 500 }}
                >
                  {formatTimeRemaining(adStatus.exemption_expires_in)} remaining
                </Typography>
              </Box>
            )}
        </Box>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mb: 2,
            fontSize: "0.9rem",
          }}
        >
          {adStatus.requires_ad
            ? "You need to watch an ad before joining rooms."
            : "You're currently exempt from watching ads."}
        </Typography>

        {adStatus.requires_ad ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              alignItems: isMobile ? "stretch" : "center",
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(211, 47, 47, 0.12)"
                  : "rgba(211, 47, 47, 0.04)",
              padding: "12px 16px",
              borderRadius: "8px",
              mb: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                mb: isMobile ? 1 : 0,
              }}
            >
              <BlockIcon sx={{ color: theme.palette.error.main, mr: 1 }} />
              <Typography
                variant="body2"
                sx={{ color: theme.palette.error.main, fontWeight: 500 }}
              >
                Ad required for next room join
              </Typography>
            </Box>

            {!isMobile && (
              <Button
                variant="outlined"
                size="small"
                color="error"
                sx={{ ml: "auto" }}
                onClick={() => {
                  if (onAdRequired) {
                    onAdRequired("watch");
                  }
                }}
              >
                Watch Now
              </Button>
            )}
          </Box>
        ) : (
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: isMobile ? "flex-start" : "center",
                justifyContent: "space-between",
                flexDirection: isMobile ? "column" : "row",
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(46, 125, 50, 0.12)"
                    : "rgba(46, 125, 50, 0.04)",
                padding: "12px 16px",
                borderRadius: "8px",
                mb: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: isMobile ? 1 : 0,
                }}
              >
                <CheckCircleIcon
                  sx={{ color: theme.palette.success.main, mr: 1 }}
                />
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.success.main, fontWeight: 500 }}
                >
                  No ad required
                </Typography>
              </Box>

              {adStatus.exemption_expires_in && isMobile && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <TimerIcon
                    sx={{
                      fontSize: "0.875rem",
                      mr: 0.5,
                      color: theme.palette.info.main,
                    }}
                  />
                  <Typography
                    variant="body2"
                    color="info.main"
                    sx={{ fontWeight: 500 }}
                  >
                    {formatTimeRemaining(adStatus.exemption_expires_in)}{" "}
                    remaining
                  </Typography>
                </Box>
              )}
            </Box>

            {adStatus.exemption_expires_in && (
              <Box sx={{ mb: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={calculateProgress()}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.08)"
                        : "rgba(0, 0, 0, 0.04)",
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: theme.palette.success.main,
                    },
                  }}
                />
              </Box>
            )}
          </Box>
        )}

        {/* Action Buttons */}
        <Box
          sx={{
            mt: 2,
            display: "flex",
            justifyContent: isMobile ? "space-between" : "center",
            flexDirection: isMobile && adStatus.requires_ad ? "column" : "row",
            gap: isMobile ? 1 : 2,
          }}
        >
          {adStatus.requires_ad && isMobile && (
            <Button
              variant="contained"
              size="medium"
              color="error"
              fullWidth
              onClick={() => {
                if (onAdRequired) {
                  onAdRequired("watch");
                }
              }}
              sx={{ mb: 1 }}
              startIcon={<OndemandVideoIcon />}
            >
              Watch Ad Now
            </Button>
          )}

          <Tooltip title="Upgrade to Standard or higher to remove ad requirements">
            <Button
              variant="contained"
              size="medium"
              fullWidth={isMobile}
              onClick={() => {
                if (onAdRequired) {
                  onAdRequired("upgrade");
                }
              }}
              color="primary"
              sx={{
                backgroundColor:
                  theme.palette.mode === "dark" ? "#d44402" : "#d44402",
                "&:hover": {
                  backgroundColor:
                    theme.palette.mode === "dark" ? "#c03e02" : "#c03e02",
                },
              }}
              startIcon={<NotificationsActiveIcon />}
            >
              Upgrade to Remove Ads
            </Button>
          </Tooltip>
        </Box>
      </CardContent>
    </AdCard>
  );
}
